/**
 * SCENE 04: THE COLLAPSE THAT BECOMES THE BRIDGE (Classical Architecture // 100 AD)
 * Placing a stone keyblock causes vault collapse into abyss, locking falling stones into an indestructible highway.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene04_CollapseBridge implements ISceneLifecycle {
  public id = 3;
  public name = 'The Collapse That Becomes The Bridge';
  public era = 'Classical Architecture (100 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private keyblockY: number = -120; // Drag keyblock down into arch slot
  private isCollapsed: boolean = false;
  private bridgeProgress: number = 0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      if (payload.dy && payload.dy > 0 && !this.isCollapsed) {
        this.keyblockY += payload.dy * 0.3;
      }
    });

    this.unsubscribeList.push(unsubMove);
  }

  public update(dt: number): void {
    if (this.keyblockY >= 0 && !this.isCollapsed) {
      this.isCollapsed = true;
      this.store.setFlag(StateBitmask.BRIDGE_FORMED, true);
      this.eventBus.emit(EventType.AUDIO_TRIGGER_FRICTION, 0, 0, 0, 0, 200);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 3,
        'Vault collapses into abyss! Debris locks together into a permanent stone highway across the canyon.'
      );
    }

    if (this.isCollapsed && this.bridgeProgress < 1.0) {
      this.bridgeProgress = Math.min(1.0, this.bridgeProgress + dt * 2.0);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    // Abyss Depth Gradient
    const abyssGrad = ctx.createLinearGradient(0, cy, 0, height);
    abyssGrad.addColorStop(0, '#100e0d');
    abyssGrad.addColorStop(1, '#000000');
    ctx.fillStyle = abyssGrad;
    ctx.fillRect(0, cy, width, height / 2);

    if (this.isCollapsed) {
      // Solid Stone Highway Bridge formed across abyss
      ctx.fillStyle = '#2d2925';
      ctx.fillRect(0, cy + 20, width * this.bridgeProgress, 35);

      ctx.fillStyle = '#a09080';
      ctx.font = '600 13px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('THE COLLAPSED HIGHWAY  //  INDESTRUCTIBLE PATHWAY', cx, cy + 42);
    } else {
      // Drag Keyblock Indicator
      const keyY = cy - 80 + Math.min(80, this.keyblockY);
      ctx.fillStyle = '#5a524a';
      ctx.fillRect(cx - 30, keyY, 60, 40);

      ctx.fillStyle = '#fff';
      ctx.font = '400 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('[ DRAG KEYBLOCK DOWN TO LOCK VAULT ]', cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
